import { create } from 'zustand';
import Hls from 'hls.js';
import { radioApi } from '../api/radio';
import type { Track } from '../types';
import { usePlayerStore } from './playerStore';

interface RadioState {
  // Current state
  profileSlug: string | null;
  mood: string | null;
  currentTrack: Track | null;
  nextTrack: Track | null;
  isPlaying: boolean;
  isLoading: boolean;
  isAd: boolean;
  error: string | null;

  // Time tracking for current track (driven by /current polling, not
  // the audio element — with HLS the element runs a continuous stream
  // so audio.currentTime keeps growing past one track).
  elapsed: number;
  remaining: number;
  duration: number;
  serverTimeDiff: number;

  // Audio - HTMLMediaElement fed by an HLS stream
  audio: HTMLAudioElement | null;
  volume: number;

  // Actions
  startRadio: (profileSlug: string, mood: string) => Promise<void>;
  stopRadio: () => void;
  pause: () => void;
  resume: () => void;
  setVolume: (volume: number) => void;
}

// Single audio element + hls.js instance reused across mood changes.
let globalAudio: HTMLAudioElement | null = null;
let globalHls: Hls | null = null;
// Stream URL currently attached. Safari native HLS doesn't expose a
// way to detect "is this the same stream" — we track it ourselves so
// repeat startRadio() calls (page revisits, focus events) don't tear
// down and rebuild a stream that's already playing.
let currentStreamUrl: string | null = null;
let visibilityHandler: (() => void) | null = null;
let metaPollTimer: ReturnType<typeof setInterval> | null = null;

// Whether the user wants playback running. Cleared on explicit pause/
// stop. On a media-thread pause (notification, call) we use this to
// decide whether to attempt auto-resume.
let userIntendedToPlay = false;

// True if the last pause was caused by the system (interruption,
// background tab throttling, focus loss) rather than our pause() action.
// resume() uses this to decide whether a cheap audio.play() is enough
// or whether the stream needs a full re-attach — after a system pause
// hls.js can be in a stale state where play() resolves but no bytes
// ever reach the speaker.
let wasSystemPause = false;

// AudioContext + AnalyserNode are kept around for the visualizer
// component (TV app). They are NOT in the audio output path — that
// would suspend on mobile when the tab is backgrounded.
let audioContext: AudioContext | null = null;
let analyserNode: AnalyserNode | null = null;

function logToServer(message: string, data?: Record<string, unknown>) {
  console.log('[RadioStore]', message, data);
}

function getOrCreateAudio(): HTMLAudioElement {
  if (!globalAudio) {
    globalAudio = new Audio();
    globalAudio.crossOrigin = 'anonymous';
    // Enable background playback on mobile
    globalAudio.setAttribute('playsinline', 'true');
    globalAudio.preload = 'auto';

    logToServer('Created audio element');

    try {
      const AudioContextClass =
        window.AudioContext || (window as any).webkitAudioContext;
      audioContext = new AudioContextClass();
      analyserNode = audioContext.createAnalyser();
      analyserNode.fftSize = 256;
      analyserNode.smoothingTimeConstant = 0.8;
    } catch (error) {
      logToServer('Failed to setup audio context', {
        error: String(error),
      });
    }
  }
  return globalAudio;
}

// Returned to the visualizer component. With HLS playback the analyser
// has no input source, so the visualizer renders silence — kept for
// API compatibility with the TV page.
export function getAudioAnalyser(): AnalyserNode | null {
  return analyserNode;
}

export function resumeAudioContext(): Promise<void> {
  if (audioContext?.state === 'suspended') {
    return audioContext.resume().catch(() => undefined);
  }
  return Promise.resolve();
}

// Best-effort auto-resume after a system interruption. Runs only inside
// media-thread events (onpause / oncanplay) so the call lands while a
// locked phone has main-thread timers frozen.
function tryResumeAfterInterruption() {
  if (!userIntendedToPlay || !globalAudio) return;
  if (!globalAudio.paused || globalAudio.ended) return;

  globalAudio.play().catch(() => {
    // Interruption still active — oncanplay will fire later.
  });
}

// Detach any existing hls.js instance / audio source.
function detachStream() {
  if (globalHls) {
    try {
      globalHls.destroy();
    } catch {
      // ignore
    }
    globalHls = null;
  }
  if (globalAudio) {
    try {
      globalAudio.removeAttribute('src');
      globalAudio.load();
    } catch {
      // ignore
    }
  }
  currentStreamUrl = null;
}

// Attach an HLS stream to the audio element. Returns a promise that
// resolves when the player is ready to play (manifest parsed for hls.js,
// canplay for native HLS).
//
// Detection order matters: hls.js first, native only if hls.js can't
// run. Chrome (desktop + Android) reports `canPlayType('application/
// vnd.apple.mpegurl') === 'maybe'` even though its native HLS support
// is unreliable — preferring hls.js whenever it's supported avoids
// that trap and only falls through to native on Safari, which is where
// hls.js itself isn't available (no MSE for audio in iOS Safari).
function attachStream(
  audio: HTMLAudioElement,
  streamUrl: string
): Promise<void> {
  detachStream();
  currentStreamUrl = streamUrl;

  if (Hls.isSupported()) {
    logToServer('Attaching hls.js', { streamUrl });
    const hls = new Hls({
      // The playlist updates every request (live, no ENDLIST) so let
      // hls.js refresh it on its normal cadence. We don't need low-
      // latency live mode for 3-5 minute segments.
      lowLatencyMode: false,
      // EXT-X-START hint in our playlist sets the offset into the
      // current track; -1 means "respect playlist hints".
      startPosition: -1,
    });
    globalHls = hls;

    // Persistent error handler — only soft recoveries. We DO NOT
    // destroy hls.js here even on unrecoverable fatals, because doing
    // so leaves the audio element with no data source and an in-flight
    // play() can resolve silently. If the audio actually stops, our
    // onpause -> wasSystemPause path kicks in and resume() re-attaches
    // with a fresh hls.js instance.
    hls.on(Hls.Events.ERROR, (_event, data) => {
      if (!data?.fatal) return;
      console.warn('[Radio] hls fatal error', data);
      try {
        if (data.type === Hls.ErrorTypes.NETWORK_ERROR) {
          hls.startLoad();
        } else if (data.type === Hls.ErrorTypes.MEDIA_ERROR) {
          hls.recoverMediaError();
        }
        // Other fatal types: don't touch hls — let it sit. If it stops
        // producing audio, the user-resume / system-resume path will
        // notice and rebuild the stream.
      } catch (recoveryErr) {
        console.error('[Radio] hls recovery threw', recoveryErr);
      }
    });

    return new Promise((resolve, reject) => {
      const onParsed = () => {
        hls.off(Hls.Events.MANIFEST_PARSED, onParsed);
        hls.off(Hls.Events.ERROR, onInitError);
        resolve();
      };
      const onInitError = (_e: unknown, data: any) => {
        if (data?.fatal) {
          hls.off(Hls.Events.MANIFEST_PARSED, onParsed);
          hls.off(Hls.Events.ERROR, onInitError);
          reject(new Error(`HLS fatal: ${data?.type ?? 'unknown'}`));
        }
      };
      hls.on(Hls.Events.MANIFEST_PARSED, onParsed);
      hls.on(Hls.Events.ERROR, onInitError);
      hls.loadSource(streamUrl);
      hls.attachMedia(audio);
    });
  }

  if (audio.canPlayType('application/vnd.apple.mpegurl')) {
    logToServer('Attaching native HLS', { streamUrl });
    return new Promise((resolve, reject) => {
      const onCanPlay = () => {
        audio.removeEventListener('canplay', onCanPlay);
        audio.removeEventListener('error', onError);
        resolve();
      };
      const onError = () => {
        audio.removeEventListener('canplay', onCanPlay);
        audio.removeEventListener('error', onError);
        reject(new Error('Native HLS load failed'));
      };
      audio.addEventListener('canplay', onCanPlay, { once: true });
      audio.addEventListener('error', onError, { once: true });
      audio.src = streamUrl;
      audio.load();
    });
  }

  return Promise.reject(new Error('HLS not supported in this browser'));
}

// Sync the audio element's event handlers exactly once per element.
let handlersWired = false;
function wireAudioHandlers(
  audio: HTMLAudioElement,
  set: (partial: Partial<RadioState>) => void
) {
  if (handlersWired) return;
  handlersWired = true;

  audio.onplay = () => {
    wasSystemPause = false;
    set({ isPlaying: true, error: null });
  };

  audio.onpause = () => {
    set({ isPlaying: false });
    if (audio.ended) return;
    // userIntendedToPlay is cleared inside our pause() action before
    // audio.pause() runs, so this branch only fires when the system (or
    // some other code path) paused us against our will.
    if (userIntendedToPlay) {
      wasSystemPause = true;
      tryResumeAfterInterruption();
    }
  };

  audio.oncanplay = () => {
    // Fires from the media thread (works on a locked screen).
    if (
      userIntendedToPlay &&
      audio.paused &&
      !audio.ended
    ) {
      tryResumeAfterInterruption();
    }
  };

  audio.onerror = () => {
    console.error('[Radio] audio.error', audio.error);
  };
}

// Periodically poll /current so the UI knows which track is playing,
// how much is left, etc. We do NOT drive playback from this — hls.js /
// the audio element handle the bytes. Polling is foreground-only on
// mobile (background timers are throttled), which is fine because the
// UI isn't visible there.
function startMetadataPolling(
  profileSlug: string,
  mood: string,
  set: (partial: Partial<RadioState>) => void,
  get: () => RadioState
) {
  stopMetadataPolling();

  const poll = async () => {
    const state = get();
    if (state.profileSlug !== profileSlug || state.mood !== mood) {
      stopMetadataPolling();
      return;
    }
    try {
      const data = await radioApi.getCurrentTrack(profileSlug, mood);
      const fresh = get();
      if (fresh.profileSlug !== profileSlug || fresh.mood !== mood) return;

      const serverDate = new Date(data.serverTime);
      const serverTimeDiff = serverDate.getTime() - Date.now();

      set({
        currentTrack: data.current.track,
        nextTrack: data.next?.track || null,
        duration: data.current.duration,
        elapsed: data.current.elapsed,
        remaining: data.current.remaining,
        serverTimeDiff,
        error: null,
        isAd: data.current.kind === 'ad',
      });
      setupMediaSession(data.current.track, get);
    } catch (err) {
      // Soft fail — the audio keeps playing even if metadata fetch
      // hiccups.
      console.warn('[Radio] metadata poll failed:', err);
    }
  };

  // Run immediately, then on an interval. 4s is a good compromise
  // between freshness and request volume given track durations are
  // measured in minutes.
  void poll();
  metaPollTimer = setInterval(poll, 4000);
}

function stopMetadataPolling() {
  if (metaPollTimer) {
    clearInterval(metaPollTimer);
    metaPollTimer = null;
  }
}

// Visibility handler — sync isPlaying state when the page comes back.
function setupVisibilityHandler(
  set: (partial: Partial<RadioState>) => void,
  get: () => RadioState
) {
  if (visibilityHandler) {
    document.removeEventListener('visibilitychange', visibilityHandler);
  }

  visibilityHandler = () => {
    if (document.visibilityState === 'visible') {
      const { audio } = get();
      if (audio) {
        set({ isPlaying: !audio.paused && !audio.ended });
      }
    }
  };

  document.addEventListener('visibilitychange', visibilityHandler);
}

export const useRadioStore = create<RadioState>((set, get) => ({
  profileSlug: null,
  mood: null,
  currentTrack: null,
  nextTrack: null,
  isPlaying: false,
  isLoading: false,
  isAd: false,
  error: null,
  elapsed: 0,
  remaining: 0,
  duration: 0,
  serverTimeDiff: 0,
  audio: null,
  volume: 0.8,

  startRadio: async (profileSlug: string, mood: string) => {
    const streamUrl = radioApi.getStreamUrl(profileSlug, mood);
    console.log('[Radio] startRadio called:', {
      profileSlug,
      mood,
      hadAudio: !!globalAudio,
      hadHls: !!globalHls,
      attached: currentStreamUrl,
      requesting: streamUrl,
    });

    // Same stream already attached? Just make sure it's playing — no
    // need to tear down and rebuild (which causes a visible "loading"
    // flash, including when iOS brings a backgrounded Safari tab back
    // to the foreground and re-runs RadioPlayerPage's mount effect).
    const cur = get();
    if (
      cur.profileSlug === profileSlug &&
      cur.mood === mood &&
      currentStreamUrl === streamUrl &&
      globalAudio
    ) {
      userIntendedToPlay = true;
      if (globalAudio.paused) {
        try {
          await globalAudio.play();
        } catch {
          /* autoplay may need a gesture */
        }
      }
      return;
    }

    userIntendedToPlay = true;

    try {
      sessionStorage.setItem('radio-gesture-unlocked', '1');
    } catch {
      // sessionStorage may be unavailable (private mode) — fall back
      // to needing a gesture on every navigation, which is acceptable.
    }

    // Stop the track player if it's playing
    usePlayerStore.getState().stop();

    // Touch the audio element + context inside the user gesture so the
    // browser unlocks autoplay for subsequent async work.
    const audio = getOrCreateAudio();
    audio.volume = get().volume;
    wireAudioHandlers(audio, set);
    resumeAudioContext();
    setupVisibilityHandler(set, get);

    set({
      isLoading: true,
      error: null,
      profileSlug,
      mood,
      audio,
    });

    setupMediaSessionHandlers(get);

    try {
      await attachStream(audio, streamUrl);

      // Verify we're still on the requested mood after the async wait.
      const after = get();
      if (after.profileSlug !== profileSlug || after.mood !== mood) {
        return;
      }

      try {
        await audio.play();
      } catch (err) {
        // Autoplay might be blocked — surface it via state so the UI
        // can show a play button.
        console.warn('[Radio] play() rejected:', err);
        set({ isPlaying: false });
      }

      set({ isLoading: false });
      startMetadataPolling(profileSlug, mood, set, get);
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Failed to start radio';
      console.error('[Radio] startRadio failed:', err);
      set({ isLoading: false, error: message });
    }
  },

  stopRadio: () => {
    userIntendedToPlay = false;
    stopMetadataPolling();
    detachStream();

    if ('mediaSession' in navigator) {
      navigator.mediaSession.metadata = null;
    }

    set({
      profileSlug: null,
      mood: null,
      currentTrack: null,
      nextTrack: null,
      isPlaying: false,
      audio: null,
      elapsed: 0,
      remaining: 0,
      isAd: false,
    });
  },

  pause: () => {
    userIntendedToPlay = false;
    wasSystemPause = false;
    const { audio } = get();
    if (audio) {
      audio.pause();
      set({ isPlaying: false });
    }
  },

  resume: async () => {
    const { audio, profileSlug, mood } = get();
    if (!audio || !profileSlug || !mood) return;

    userIntendedToPlay = true;

    // hls.js path (Chrome desktop/Android, Firefox, Edge): always
    // re-attach on resume. After any kind of background gap the loader
    // is often stale and audio.play() resolves silently — the user
    // sees isPlaying=true but no sound. Re-attaching is cheap with
    // server-side EXT-X-START seeking us back to the current live
    // position, and guarantees the audio actually plays.
    if (Hls.isSupported()) {
      console.log('[Radio] resume: re-attaching hls.js stream');
      wasSystemPause = false;
      currentStreamUrl = null;
      get().startRadio(profileSlug, mood);
      return;
    }

    // Native HLS (Safari iOS): try cheap play(); full re-attach on
    // either explicit failure or a known-stale state.
    try {
      await audio.play();
      resumeAudioContext();
      if (wasSystemPause) {
        wasSystemPause = false;
        // Best-effort: keep the element rolling, but if it doesn't
        // actually produce audio there's not much more we can do on
        // native HLS without tearing down the element.
      }
    } catch (err) {
      console.error('[Radio] resume failed:', err);
      wasSystemPause = false;
      currentStreamUrl = null;
      get().startRadio(profileSlug, mood);
    }
  },

  setVolume: (volume: number) => {
    const clamped = Math.max(0, Math.min(1, volume));
    const { audio } = get();
    if (audio) {
      audio.volume = clamped;
    }
    set({ volume: clamped });
  },
}));

/**
 * Media Session metadata — refreshed on each metadata poll so lock-screen
 * UI tracks the currently playing song.
 */
function setupMediaSession(track: Track, get: () => RadioState) {
  if (!('mediaSession' in navigator)) return;

  const artworkSrc = track.image || '/landing/app-icon-512.png';
  const artworkType = track.image ? 'image/jpeg' : 'image/png';

  navigator.mediaSession.metadata = new MediaMetadata({
    title: track.name || 'Fonorex Radio',
    artist: 'Fonorex',
    album: get().mood?.replace(/-/g, ' ') || 'Radio',
    artwork: [
      { src: artworkSrc, sizes: '96x96', type: artworkType },
      { src: artworkSrc, sizes: '128x128', type: artworkType },
      { src: artworkSrc, sizes: '192x192', type: artworkType },
      { src: artworkSrc, sizes: '256x256', type: artworkType },
      { src: artworkSrc, sizes: '384x384', type: artworkType },
      { src: artworkSrc, sizes: '512x512', type: artworkType },
    ],
  });
}

let mediaSessionHandlersWired = false;
function setupMediaSessionHandlers(get: () => RadioState) {
  if (mediaSessionHandlersWired) return;
  if (!('mediaSession' in navigator)) return;
  mediaSessionHandlersWired = true;

  navigator.mediaSession.setActionHandler('play', () => {
    get().resume();
  });
  navigator.mediaSession.setActionHandler('pause', () => {
    get().pause();
  });
  // Radio doesn't support seeking or track skipping.
  navigator.mediaSession.setActionHandler('nexttrack', null);
  navigator.mediaSession.setActionHandler('previoustrack', null);
  navigator.mediaSession.setActionHandler('seekforward', null);
  navigator.mediaSession.setActionHandler('seekbackward', null);
}
